import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';

import { Spinner } from '@agconnections/grow-ui';
import {
  Popover,
  Button,
  ButtonType,
  Input
} from 'syngenta-digital-cropwise-react-ui-kit';

import PlusIcon from 'assets/plus-current-color.svg?react';
import SearchIcon from 'assets/search.svg?react';
import useDebounce from 'hooks/useDebounce';
import { Context } from 'components/Store';
import { RESET_MASS_ASSIGNER } from 'reducers/reducer';
import SeasonCreateModal from 'components/Modals/SeasonCreateModal';
import { selectCropSeason } from 'utilities/updated-permissions';
import { useFlags } from 'launchdarkly-react-client-sdk';
import featureFlagRenderItem from 'helpers/featureFlagRenderItem';
import useCropSeasonsListApi from 'hooks/useCropSeasonsListApi';
import { AmplitudeContext } from 'utilities/amplitude/useAmplitude';
import BreadCrumbItem from '../../../App/AppShell/components/Breadcrumb/BreadCrumbItem';

// Custom hooks
import useCropSeasonData from './hooks/useCropSeasonData';
import useCropSeasonSelection from './hooks/useCropSeasonSelection';

// Components
import CropSeasonSections from './components/CropSeasonSections';

const BreadcrumbCropSeasons = ({
  getData,
  isLast,
  onSelect,
  searchPlaceholder,
  title,
  disabled,
  disabledCreate,
  allowMultipleSelection = false,
  validationBeforeSet
}) => {
  // State
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchText, setSearchText] = useState(null);
  const [openCropSeasonNewModal, setOpenCropSeasonNewModal] = useState(false);

  // Context and hooks
  const [{ organization, cropSeasons }, dispatch] = useContext(Context);
  const amplitude = useContext(AmplitudeContext);
  const debouncedSearchTerm = useDebounce(searchText, 300);
  const {
    releaseFeCondensedCropSeasons,
    releaseFeCondensedCropSeasonsByOrg
  } = useFlags();

  const isCondensedCropSeasonEnabled = featureFlagRenderItem(
    releaseFeCondensedCropSeasons,
    releaseFeCondensedCropSeasonsByOrg,
    organization?.id
  );
  const seasonsApi = useCropSeasonsListApi(isCondensedCropSeasonEnabled);

  // Custom hooks for data management
  const { items, isLoading } = useCropSeasonData(
    getData,
    debouncedSearchTerm,
    isMenuOpen
  );
  const {
    selectedInactiveSeason,
    handleActiveSeasonChange,
    handleInactiveSeasonChange,
    handleUnassignedFieldsChange,
    isSeasonSelected,
    seasonTitle,
    getSelectedSeasons,
    applySelections,
    cancelSelections
  } = useCropSeasonSelection(allowMultipleSelection, cropSeasons, isMenuOpen);

  const closeMenu = open => {
    if (open) return;

    setIsMenuOpen(false);
    setSearchText(null);
  };

  const openAddCropSeasonModal = event => {
    amplitude.sendEventToAmplitude(
      amplitude.events.epic.CropSeason.createCropSeason,
      {
        page: amplitude.events.epic.CropSeason.pages.breadcrumbDropdown
      }
    );
    event.stopPropagation();
    closeMenu();
    setOpenCropSeasonNewModal(true);
  };

  const setItemState = () => {
    // Get selected seasons WITHOUT saving to localStorage yet
    const selectedSeasons = getSelectedSeasons();

    // If validation function is provided, check if we should save
    let shouldSave = true;
    if (validationBeforeSet) {
      shouldSave = validationBeforeSet(selectedSeasons);
    }

    // Only apply and save if validation passed
    if (shouldSave) {
      applySelections(); // Now save to localStorage
      if (allowMultipleSelection || selectedSeasons.length > 0) {
        onSelect(selectedSeasons);
      }
    }

    // Always close the menu
    closeMenu(false);
  };

  const handleCancel = () => {
    cancelSelections(); // Revert to saved state
    closeMenu(false);
  };

  const handleCreateSeason = async season => {
    await selectCropSeason(
      dispatch,
      season.id,
      isCondensedCropSeasonEnabled,
      seasonsApi
    );
    onSelect(season.id);
  };

  const handleTriggerClick = () => {
    if (disabled) return;
    setIsMenuOpen(isOpen => !isOpen);
    setSearchText(null);
  };

  const handleTriggerKeyDown = event => {
    if (disabled) return;
    if (event.nativeEvent.key === 'Enter') {
      setIsMenuOpen(isOpen => !isOpen);
      setSearchText(null);
    }
  };

  const isApplyDisabled = getSelectedSeasons().length === 0;

  return (
    <>
      <Popover
        trigger="click"
        placement="bottomLeft"
        align={{ offset: [0, 8] }}
        open={isMenuOpen}
        onOpenChange={closeMenu}
        content={
          <div
            data-testid="breadcrumb-popup"
            style={{ width: 322, height: disabledCreate ? 373 : 416 }}
            className="overflow-auto text-left"
          >
            <div className="px-3 py-10px font-semibold">Crop Seasons</div>
            <div className="px-4 py-2">
              <Input
                prefix={<SearchIcon />}
                autoComplete="off"
                type="text"
                name="search_breadcrumb"
                placeholder={searchPlaceholder}
                value={searchText}
                onChange={event => {
                  setSearchText(event.target.value);
                }}
              />
            </div>
            <div className="px-4 py-2">
              {!isLoading && !disabled ? (
                <CropSeasonSections
                  items={items}
                  allowMultipleSelection={allowMultipleSelection}
                  isSeasonSelected={isSeasonSelected}
                  handleActiveSeasonChange={handleActiveSeasonChange}
                  handleInactiveSeasonChange={handleInactiveSeasonChange}
                  handleUnassignedFieldsChange={handleUnassignedFieldsChange}
                  selectedInactiveSeason={selectedInactiveSeason}
                />
              ) : (
                <Spinner />
              )}
            </div>
            <div className="pt-2 pb-10px px-4 flex justify-end">
              <div className="mr-2">
                <Button
                  type={ButtonType.outline}
                  data-testid="cancel-button"
                  onClick={event => {
                    event.stopPropagation();
                    handleCancel();
                  }}
                >
                  Cancel
                </Button>
              </div>
              <Button
                type={ButtonType.primary}
                data-testid="apply-button"
                disabled={isApplyDisabled}
                onClick={event => {
                  event.stopPropagation();
                  dispatch({ type: RESET_MASS_ASSIGNER });
                  setItemState();
                }}
              >
                Apply
              </Button>
            </div>
            {!disabledCreate && (
              <div className="w-full text-left rounded-b bg-white py-2 px-3 border-t border-neutral-200">
                <Button
                  data-testid="add-crop-season-button"
                  type={ButtonType.link}
                  className="px-0"
                  onClick={event => openAddCropSeasonModal(event)}
                  icon={<PlusIcon />}
                >
                  Create New Crop Season
                </Button>
              </div>
            )}
          </div>
        }
      >
        <div
          data-testid="nav-menu-head"
          className={`flex items-center  flex-shrink-0 focus:border-none text-white
    ${disabled ? 'pointer-events-none' : ''}`}
          role="button"
          tabIndex={0}
          onClick={handleTriggerClick}
          onKeyDown={handleTriggerKeyDown}
          disabled={disabled}
        >
          <BreadCrumbItem
            title={title}
            value={seasonTitle}
            isLast={isLast}
            disabled={disabled}
          />
        </div>
      </Popover>
      <SeasonCreateModal
        open={openCropSeasonNewModal}
        creationFromPage={
          amplitude.events.epic.CropSeason.pages.breadcrumbDropdown
        }
        onClose={() => setOpenCropSeasonNewModal(false)}
        onSuccess={handleCreateSeason}
      />
    </>
  );
};

BreadcrumbCropSeasons.defaultProps = {
  searchPlaceholder: 'Search',
  isLast: false,
  disabled: false,
  disabledCreate: false,
  allowMultipleSelection: false,
  validationBeforeSet: null
};

BreadcrumbCropSeasons.propTypes = {
  getData: PropTypes.func.isRequired,
  isLast: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  searchPlaceholder: PropTypes.string,
  title: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  disabledCreate: PropTypes.bool,
  allowMultipleSelection: PropTypes.bool,
  validationBeforeSet: PropTypes.func
};

export default BreadcrumbCropSeasons;
